import React from "react";
import { Box, Typography, Tooltip, IconButton, Chip } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";

const SummaryRow = ({ label, children }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "260px minmax(0, 1fr)" },
      alignItems: "center",
      columnGap: 2,
      rowGap: 0.5,
      mb: "16px",
    }}
  >
    <Typography component="div" sx={{ fontWeight: 700 }}>
      {label}:
    </Typography>
    <Box sx={{ minWidth: 0 }}>{children}</Box>
  </Box>
);

const ParticipantConfirmationSummary = ({
  matchedEhr,
  existingPatient,
  scheduledAppointmentTime,
  fullName,
  email,
  emailContent,
  firstName,
  lastName,
  birthDate,
  mobilePhoneNumber,
  mobilePhoneNumberContent,
  homeAddressLine1,
  homeAddressLine2,
  city,
  addressState,
  zipCode,
  hasNoStudies,
  activeStudies = [],
  incompleteStudies = [],
  onChipClick,
  contactOrder = "email-phone",
  children
}) => {
  const emailRow = (
    <SummaryRow label="Email">
      {emailContent || email}
    </SummaryRow>
  );
  const phoneRow = (mobilePhoneNumberContent || (mobilePhoneNumber && mobilePhoneNumber !== "N/A")) ? (
    <SummaryRow label="Phone Number">
      {mobilePhoneNumberContent || mobilePhoneNumber}
    </SummaryRow>
  ) : null;

  return (
    <Box sx={{ width: "90%", marginX: "auto" }}>
      {matchedEhr && (
        <SummaryRow label="Medical Record Number">
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {matchedEhr}
            <Tooltip
              title={
                <Typography sx={{ fontSize: "13px", color: "#fff", px: 1 }}>
                  {existingPatient
                    ? "Participant Medical Record Number should be identical to the Participant's EHR number."
                    : "The Participant EHR Number will now be the Participant Medical Record Number in the Texas A&M Health System."}
                </Typography>
              }
              arrow
              placement="top"
              slotProps={{
                tooltip: {
                  sx: {
                    backgroundColor: "var(--tamu-maroon)",
                    boxShadow: "0px 3px 6px rgba(0,0,0,0.2)",
                    maxWidth: 250,
                  },
                },
              }}
            >
              <IconButton
                size="small"
                sx={{
                  color: "var(--tamu-maroon)",
                  ml: 1,
                  padding: 0.5,
                }}
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </SummaryRow>
      )}

      {fullName ? (
        <SummaryRow label="Name">{fullName}</SummaryRow>
      ) : (
        <>
          <SummaryRow label="First Name">{firstName}</SummaryRow>
          <SummaryRow label="Last Name">{lastName}</SummaryRow>
        </>
      )}

      <SummaryRow label="Date of Birth">{birthDate}</SummaryRow>

      {scheduledAppointmentTime && (
        <SummaryRow label="Scheduled Time">
          {scheduledAppointmentTime}
        </SummaryRow>
      )}

      {children}

      {phoneRow}
      
      {emailRow}
      
      {(homeAddressLine1 || city || addressState || zipCode) && (
        <SummaryRow label="Home Address">
          {[
            homeAddressLine1,
            homeAddressLine2,
            [city, addressState, zipCode].filter(Boolean).join(", "),
          ]
            .filter(Boolean)
            .join(", ")}
        </SummaryRow>
      )}

      <SummaryRow label="Active Consents">
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {hasNoStudies || activeStudies.length === 0 ? (
            <Chip
              label="None"
              size="small"
              sx={{
                backgroundColor: "#e0e0e0",
                color: "#666",
                fontWeight: 600,
                px: "10px",
                py: "2px",
              }}
            />
          ) : (
            activeStudies.map((study, index) => (
              <Chip
                key={`active-${study.irbStudy}-${index}`}
                label={`${study.studyName} (${study.irbStudy})`}
                size="small"
                onClick={onChipClick}
                sx={{
                  cursor: onChipClick ? "pointer" : "default",
                  backgroundColor: "#4caf50",
                  color: "white",
                  fontWeight: 600,
                  px: "10px",
                  py: "2px",
                  "&:hover": { backgroundColor: onChipClick ? "#45a049" : "#4caf50" },
                }}
              />
            ))
          )}
        </Box>
      </SummaryRow>

      {incompleteStudies.length > 0 && (
        <SummaryRow label="Incomplete Consents">
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {incompleteStudies.map((study, index) => (
              <Chip
                key={`incomplete-${study.irbStudy}-${index}`}
                label={onChipClick ? `${study.studyName} (${study.irbStudy})` : `Inactive: ${study.studyName} (${study.irbStudy})`}
                size="small"
                onClick={onChipClick}
                sx={{
                  cursor: onChipClick ? "pointer" : "default",
                  backgroundColor: "#c62828",
                  color: "white",
                  fontWeight: 600,
                  px: "10px",
                  py: "2px",
                  "&:hover": { backgroundColor: onChipClick ? "#b71c1c" : "#c62828" },
                }}
              />
            ))}
          </Box>
        </SummaryRow>
      )}
    </Box>
  );
};

export default ParticipantConfirmationSummary;
